<template>
  <div class="row">
    <!-- Left Column: Image and Identification -->
    <div class="col-xl-4 col-lg-5 mb-4">
      <div class="card border-0 shadow-sm rounded-3 h-100">
        <div class="card-header bg-transparent border-bottom-0 pt-4 pb-0 text-center">
          <h6 class="fw-semibold mb-0">Product Visuals</h6>
        </div>
        <div class="card-body d-flex flex-column align-items-center justify-content-center p-xl-5">
          <div class="single-product-img text-center mb-4 p-4 bg_ash rounded-3 w-100 d-flex justify-content-center align-items-center" style="min-height: 250px;">
            <img :src="imageUrl" alt="Product" class="img-fluid" style="max-height: 200px; object-fit: contain;" v-if="imageUrl" />
            <div v-else class="text-muted d-flex flex-column align-items-center">
              <img src="../../../assets/img/icons/category.svg" width="48" class="mb-2 opacity-50" />
              <span>No Image Available</span>
            </div>
          </div>
          
          <div class="w-100 d-flex flex-column align-items-center bg_ash rounded-3 p-4">
            <h6 class="fs-14 fw-semibold text-muted mb-3">Identification Codes</h6>
            <div class="bg-white p-3 rounded-2 w-100 d-flex justify-content-center mb-3 shadow-sm">
              <BarcodeGenerator v-if="product?.code" :value="product.code" :height="60" :width="1.8" />
              <span v-else class="text-muted fs-12">No Barcode</span>
            </div>
            <div class="bg-white p-3 rounded-2 w-100 d-flex justify-content-center shadow-sm">
              <QRCodeGenerator v-if="product?.code" :value="product.code" :size="120" />
              <span v-else class="text-muted fs-12">No QR Code</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Column: Details & Stock -->
    <div class="col-xl-8 col-lg-7">
      
      <!-- Primary Details Card -->
      <div class="card border-0 shadow-sm rounded-3 mb-4">
        <div class="card-header bg-transparent border-bottom-0 pt-4 pb-0">
          <h6 class="fw-semibold mb-0">General Information</h6>
        </div>
        <div class="card-body p-xl-4">
          <ul class="list-group list-group-flush border-top-0">
            <li class="list-group-item d-flex justify-content-between align-items-center px-0 py-3 border-bottom border-light">
              <span class="text-muted fs-14">Product Code</span>
              <span class="fw-semibold text-title fs-14">{{ product?.code || 'N/A' }}</span>
            </li>
            <li class="list-group-item d-flex justify-content-between align-items-center px-0 py-3 border-bottom border-light">
              <span class="text-muted fs-14">Product Name</span>
              <span class="fw-bold text-primary fs-15">{{ product?.name || 'N/A' }}</span>
            </li>
            <li class="list-group-item d-flex justify-content-between align-items-center px-0 py-3 border-bottom border-light">
              <span class="text-muted fs-14">Category</span>
              <span class="badge bg_ash text-title fs-13 py-2 px-3 fw-medium">{{ product?.category?.name || 'N/A' }}</span>
            </li>
            <li class="list-group-item d-flex justify-content-between align-items-center px-0 py-3 border-bottom border-light">
              <span class="text-muted fs-14">Brand</span>
              <span class="fw-semibold text-title fs-14">{{ product?.brand?.name || 'N/A' }}</span>
            </li>
            <li class="list-group-item d-flex justify-content-between align-items-center px-0 py-3 border-bottom border-light">
              <span class="text-muted fs-14">Base Unit</span>
              <span class="fw-semibold text-title fs-14">{{ product?.unit?.name || 'N/A' }}</span>
            </li>
            <li class="list-group-item d-flex justify-content-between align-items-center px-0 py-3 border-bottom border-light">
              <span class="text-muted fs-14">Sizes</span>
              <span class="fw-semibold text-title fs-14 text-end">{{ sizeLabel }}</span>
            </li>
            <li class="list-group-item d-flex justify-content-between align-items-center px-0 py-3 border-bottom border-light">
              <span class="text-muted fs-14">Public E-Menu</span>
              <span :class="product?.show_on_emenu ? 'badge bg-success' : 'badge bg-secondary'">
                {{ product?.show_on_emenu ? 'Enabled' : 'Disabled' }}
              </span>
            </li>
          </ul>
        </div>
      </div>

      <div class="row">
        <!-- Pricing Card -->
        <div class="col-md-6 mb-4">
          <div class="card border-0 shadow-sm rounded-3 h-100">
            <div class="card-header bg-transparent border-bottom-0 pt-4 pb-0">
              <h6 class="fw-semibold mb-0">Pricing Breakdown</h6>
            </div>
            <div class="card-body p-xl-4">
              <ul class="list-group list-group-flush border-top-0">
                <li class="list-group-item d-flex justify-content-between align-items-center px-0 py-3 border-bottom border-light">
                  <span class="text-muted fs-14">Cost</span>
                  <span class="fw-semibold text-danger fs-14">{{ formatCurrency(product?.cost) }}</span>
                </li>
                <li class="list-group-item d-flex justify-content-between align-items-center px-0 py-3 border-bottom border-light">
                  <span class="text-muted fs-14">Selling Price</span>
                  <span class="fw-bold text-success fs-15">{{ formatCurrency(product?.price) }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="col-md-6 mb-4">
          <div class="card border-0 shadow-sm rounded-3 h-100">
            <div class="card-header bg-transparent border-bottom-0 pt-4 pb-0">
              <h6 class="fw-semibold mb-0">Stock Overview</h6>
            </div>
            <div class="card-body p-xl-4">
              <div class="stock-summary-box rounded-3 px-3 py-4 mb-4">
                <div class="text-muted fs-13 mb-1">{{ stockSummaryLabel }}</div>
                <div class="fw-bold text-title fs-24">{{ totalStock }}</div>
              </div>

              <div v-if="visibleStocks.length" class="warehouse-stock-list">
                <div
                  v-for="stock in visibleStocks"
                  :key="`${stock.warehouse_id}-${stock.id || stock.product_id}`"
                  class="warehouse-stock-item d-flex justify-content-between align-items-center rounded-3 px-3 py-3 mb-2"
                >
                  <div>
                    <div class="fw-semibold text-title fs-14">
                      {{ stock.warehouse?.name || stock.warehouse_name || "Warehouse" }}
                    </div>
                    <div class="text-muted fs-12">
                      {{ stock.warehouse_code || "No code" }}
                    </div>
                  </div>
                  <span class="badge stock-badge">
                    {{ getStockQty(stock) }}
                  </span>
                </div>
              </div>

              <div v-else class="text-muted fs-14">No stock records available.</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  
  <div v-if="error" class="alert alert-danger">{{ error }}</div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import productService, { type Product } from "@/services/product";
import { useCurrentUser } from "@/composables/useCurrentUser";
import { buildStorageAssetUrl } from "@/utils/settings";
import BarcodeGenerator from "../../Common/BarcodeGenerator.vue";
import QRCodeGenerator from "../../Common/QRCodeGenerator.vue";

export default defineComponent({
  name: "ProductDetails",
  components: {
    BarcodeGenerator,
    QRCodeGenerator,
  },
  setup() {
    const { fetchCurrentUser } = useCurrentUser();
    const route = useRoute();
    const product = ref<Product | null>(null);
    const loading = ref(false);
    const error = ref("");
    const profileWarehouseId = ref<number>(0);

    const imageUrl = computed(() => {
      if (!product.value) return "";
      const mainAtt = product.value.attachments?.find(a => a.is_main === 1) || product.value.attachments?.[0];
      const filePath = mainAtt?.file_path || (mainAtt as any)?.path;
      return buildStorageAssetUrl(filePath);
    });

    const getStockQty = (stock: NonNullable<Product["stocks"]>[number]) =>
      Number(stock.qty ?? stock.quantity ?? 0);

    const visibleStocks = computed(() => {
      const stocks = product.value?.stocks || [];

      if (profileWarehouseId.value > 0) {
        return stocks.filter((stock) => Number(stock.warehouse_id) === Number(profileWarehouseId.value));
      }

      return stocks;
    });

    const totalStock = computed(() =>
      visibleStocks.value.reduce((total, stock) => total + getStockQty(stock), 0)
    );

    const stockSummaryLabel = computed(() =>
      profileWarehouseId.value > 0 ? "Stock In Your Warehouse" : "Total Stock Across Warehouses"
    );

    const sizeLabel = computed(() => {
      if (!product.value) return "N/A";

      if (product.value.product_sizes?.length) {
        const names = product.value.product_sizes
          .map((ps) => ps.size?.name)
          .filter((name): name is string => Boolean(name));

        if (names.length) {
          return names.join(", ");
        }
      }

      return product.value.size?.name || "N/A";
    });

    const formatCurrency = (amount?: number) => {
      if (typeof amount !== "number") return "N/A";
      return `$${amount.toFixed(2)}`;
    };

    const load = async (id: number) => {
      loading.value = true;
      error.value = "";
      try {
        product.value = await productService.getProduct(id);
      } catch (e) {
        console.error(e);
        error.value = "Failed to load product details.";
      } finally {
        loading.value = false;
      }
    };

    onMounted(async () => {
      const currentUser = await fetchCurrentUser(true);
      profileWarehouseId.value = Number(currentUser?.warehouse_id || 0);

      const idParam = route.query.id;
      if (typeof idParam === "string") {
        const id = parseInt(idParam, 10);
        if (!isNaN(id)) {
          load(id);
        } else {
          error.value = "Invalid product id.";
        }
      } else {
        error.value = "Missing product id.";
      }
    });

    return {
      product,
      loading,
      error,
      imageUrl,
      formatCurrency,
      visibleStocks,
      totalStock,
      stockSummaryLabel,
      getStockQty,
      sizeLabel,
    };
  },
});
</script>

<style scoped>
.stock-summary-box {
  background: #f7f9fc;
  border: 1px solid #e9edf5;
}

.warehouse-stock-item {
  background: #fbfcfe;
  border: 1px solid #edf1f7;
}

.stock-badge {
  background: #e8f7ee;
  color: #1f9254;
  font-size: 0.85rem;
  font-weight: 700;
  min-width: 56px;
}
</style>
